/**
 * AdMob service — state-machine-based, professional rebuild.
 *
 * Why the rewrite: the legacy `admob.js` had three structural bugs that left
 * interstitial + rewarded ads silently broken in production:
 *   1. `showInterstitial()` called `prepareInterstitial()` without awaiting
 *      and immediately checked `isInterstitialReady` — always false on first
 *      call (race condition).
 *   2. Interstitial + rewarded were never pre-loaded on init, so every show
 *      attempt had to wait for the SDK round-trip and most callers gave up.
 *   3. A failed load left the ad in a "stuck" state with no retry.
 *
 * This rewrite uses a small state machine per ad type:
 *     idle → loading → ready → showing → idle (auto-reload)
 *                   ↘  failed (retry with backoff, then give up)
 *
 * Native-only — does nothing in the browser. `init()` is safe to call
 * unconditionally; it returns `false` if Capacitor / AdMob isn't available.
 */

import { AdMob as Cfg } from '../../app/config.js';
import { createLogger } from '../../core/logger.js';
import { sleep } from '../../shared/promise.js';

const log = createLogger('admob');

/** @typedef {'idle' | 'loading' | 'ready' | 'showing' | 'failed'} AdState */

/**
 * @typedef {Object} AdRecord
 * @property {AdState} state
 * @property {number} lastShownAt
 * @property {number} retryAttempts
 * @property {((value: any) => void) | null} pendingResolve
 * @property {((value: any) => void) | null} pendingReward
 */

let initialised = false;
let bannerVisible = false;

/** @type {AdRecord} */
const interstitial = {
  state: 'idle', lastShownAt: 0, retryAttempts: 0,
  pendingResolve: null, pendingReward: null,
};
/** @type {AdRecord} */
const rewarded = {
  state: 'idle', lastShownAt: 0, retryAttempts: 0,
  pendingResolve: null, pendingReward: null,
};

/** Pick production or test ad units based on config. */
function unit(/** @type {keyof typeof Cfg.units} */ type) {
  return (Cfg.production ? Cfg.units : Cfg.testUnits)[type];
}

/** @returns {any | null} The Capacitor AdMob plugin, or null in the browser. */
function plugin() {
  return /** @type {any} */ (window).Capacitor?.Plugins?.AdMob ?? null;
}

/** @returns {boolean} */
export function isAvailable() {
  return plugin() != null;
}

/**
 * Initialise AdMob — request consent + register listeners + start pre-loading
 * interstitial and rewarded so they're ready when the app wants to show one.
 * Safe to call multiple times; only the first call does work.
 *
 * @returns {Promise<boolean>}
 */
export async function init() {
  if (initialised) return true;
  const AdMob = plugin();
  if (!AdMob) {
    log.info('AdMob plugin unavailable — running in browser?');
    return false;
  }
  try {
    await AdMob.initialize({
      requestTrackingAuthorization: true,
      testingDevices: [],
      initializeForTesting: !Cfg.production,
    });

    try {
      await AdMob.requestConsentInfo({});
    } catch (err) {
      log.warn('consent request failed:', /** @type {Error} */ (err).message);
    }

    wireListeners(AdMob);
    initialised = true;
    log.info('initialised');

    // Start pre-loading the heavy ad types so they're ready on first show.
    void preloadInterstitial();
    void preloadRewarded();
    return true;
  } catch (err) {
    log.warn('init failed:', err);
    return false;
  }
}

// ====================================================================
// Banner
// ====================================================================

/** INITIAL height we reserve in CSS for the AdMob banner overlay, in dp.
 *
 *  Used only as a placeholder until the banner reports its REAL size via
 *  the `bannerAdSizeChanged` event — at that point we update `--ad-banner-h`
 *  to the exact pixel value the native View occupies, so the bottom buttons
 *  sit immediately above the banner with no awkward gap. 60dp is the
 *  smallest realistic ADAPTIVE_BANNER height, so under-reserving here means
 *  for ~100ms after banner load the bottom controls might be slightly low —
 *  acceptable trade-off vs. an over-reserved permanent gap. */
const BANNER_HEIGHT_DP = 60;

/** Android nav bar margin so the banner sits ABOVE it (worst-case 3-button nav). */
const NAV_BAR_FLOOR_DP = 48;

/**
 * Show a bottom banner ad. Idempotent — calling while already visible
 * just returns true.
 * @returns {Promise<boolean>}
 */
export async function showBanner() {
  const AdMob = plugin();
  if (!AdMob || !initialised) return false;
  if (bannerVisible) return true;
  try {
    // Lift the banner above the system nav bar so it doesn't sit beneath it.
    await AdMob.showBanner({
      adId: unit('banner'),
      position: 'BOTTOM_CENTER',
      margin: NAV_BAR_FLOOR_DP,
      isTesting: !Cfg.production,
    });
    bannerVisible = true;
    // Reserve space in the HTML layout for the banner overlay.
    document.documentElement.style.setProperty(
      '--ad-banner-h', BANNER_HEIGHT_DP + 'px',
    );
    return true;
  } catch (err) {
    log.warn('showBanner failed:', err);
    return false;
  }
}

export async function hideBanner() {
  const AdMob = plugin();
  if (!AdMob || !bannerVisible) return;
  try { await AdMob.hideBanner(); } catch { /* ignore */ }
  bannerVisible = false;
  document.documentElement.style.setProperty('--ad-banner-h', '0px');
}

// ====================================================================
// Interstitial
// ====================================================================

/**
 * Show an interstitial. Returns true if shown, false otherwise (rate limited,
 * still loading, or load failed permanently).
 * @returns {Promise<boolean>}
 */
export async function showInterstitial() {
  const AdMob = plugin();
  if (!AdMob || !initialised) return false;

  const now = Date.now();
  if (now - interstitial.lastShownAt < Cfg.rateLimits.interstitialMs) {
    log.info('interstitial rate-limited');
    return false;
  }

  // Wait briefly for an in-flight preload if the ad isn't ready yet.
  if (interstitial.state === 'loading') {
    log.info('interstitial still loading — waiting up to 4s');
    await waitForState(interstitial, 'ready', 4_000);
  }
  if (interstitial.state !== 'ready') {
    log.info('interstitial not ready (state:', interstitial.state, ')');
    // Kick off another preload so future calls can succeed.
    void preloadInterstitial();
    return false;
  }

  try {
    interstitial.state = 'showing';
    interstitial.lastShownAt = now;
    await AdMob.showInterstitial();
    // SDK fires `interstitialAdDismissed` when the user closes; preload below.
    return true;
  } catch (err) {
    log.warn('showInterstitial failed:', err);
    interstitial.state = 'failed';
    void preloadInterstitial();
    return false;
  }
}

async function preloadInterstitial() {
  const AdMob = plugin();
  if (!AdMob) return;
  if (interstitial.state === 'loading' || interstitial.state === 'ready') return;
  interstitial.state = 'loading';
  try {
    await AdMob.prepareInterstitial({
      adId: unit('interstitial'),
      isTesting: !Cfg.production,
    });
    // `prepareInterstitial` only resolves once the ad is loaded, so flip
    // state immediately instead of waiting for the `interstitialAdLoaded`
    // event (which we still listen for as a fallback).
    interstitial.state = 'ready';
    interstitial.retryAttempts = 0;
    log.info('interstitial preloaded → ready');
  } catch (err) {
    log.warn('prepareInterstitial threw:', err);
    handleLoadFailure(interstitial, preloadInterstitial);
  }
}

// ====================================================================
// Rewarded
// ====================================================================

/**
 * Show a rewarded ad. Resolves with `{ rewarded, skipped }`. `skipped` means
 * the ad wasn't shown at all (rate-limit / not ready / failed); `rewarded`
 * means the user watched it to the reward callback.
 * @returns {Promise<{ rewarded: boolean; skipped: boolean }>}
 */
export async function showRewarded() {
  const AdMob = plugin();
  if (!AdMob || !initialised) return { rewarded: false, skipped: true };

  const now = Date.now();
  if (now - rewarded.lastShownAt < Cfg.rateLimits.rewardedMs) {
    log.info('rewarded rate-limited');
    return { rewarded: false, skipped: true };
  }

  if (rewarded.state === 'loading') {
    log.info('rewarded still loading — waiting up to 6s');
    await waitForState(rewarded, 'ready', 6_000);
  }
  if (rewarded.state !== 'ready') {
    log.info('rewarded not ready');
    void preloadRewarded();
    return { rewarded: false, skipped: true };
  }

  return new Promise((resolve) => {
    rewarded.pendingResolve = resolve;
    rewarded.state = 'showing';
    rewarded.lastShownAt = now;
    AdMob.showRewardVideoAd()
      .catch((/** @type {Error} */ err) => {
        log.warn('showRewardVideoAd failed:', err);
        rewarded.state = 'failed';
        rewarded.pendingResolve = null;
        resolve({ rewarded: false, skipped: true });
        void preloadRewarded();
      });
  });
}

async function preloadRewarded() {
  const AdMob = plugin();
  if (!AdMob) return;
  if (rewarded.state === 'loading' || rewarded.state === 'ready') return;
  rewarded.state = 'loading';
  try {
    if (typeof AdMob.prepareRewardVideoAd === 'function') {
      await AdMob.prepareRewardVideoAd({
        adId: unit('rewarded'),
        isTesting: !Cfg.production,
      });
    } else if (typeof AdMob.prepareRewardedAd === 'function') {
      await AdMob.prepareRewardedAd({
        adId: unit('rewarded'),
        isTesting: !Cfg.production,
      });
    } else {
      throw new Error('AdMob plugin: no prepareRewardVideoAd method');
    }
    // Same reasoning as preloadInterstitial — flip state directly after the
    // prepare promise resolves rather than depending on the listener.
    rewarded.state = 'ready';
    rewarded.retryAttempts = 0;
    log.info('rewarded preloaded → ready');
  } catch (err) {
    log.warn('prepareRewarded threw:', err);
    handleLoadFailure(rewarded, preloadRewarded);
  }
}

// ====================================================================
// Internals
// ====================================================================

/** @param {any} AdMob */
function wireListeners(AdMob) {
  // Banner — failed loads silently reset state so a future show can retry.
  AdMob.addListener?.('bannerAdLoaded', () => log.info('banner loaded'));
  AdMob.addListener?.('bannerAdFailedToLoad', (/** @type {any} */ e) => {
    log.warn('banner failed:', e?.message ?? e);
    bannerVisible = false;
    document.documentElement.style.setProperty('--ad-banner-h', '0px');
  });
  // The native banner reports its actual measured size via `bannerAdSizeChanged`
  // (ADAPTIVE_BANNER varies 50-110dp by device width). Use that real value
  // for `--ad-banner-h` so bottom controls sit immediately above the banner
  // — no permanent over-reserved gap, no slipping behind on wider screens.
  AdMob.addListener?.('bannerAdSizeChanged', (/** @type {{ width: number; height: number }} */ size) => {
    const h = Number(size?.height) || 0;
    if (h > 0) {
      document.documentElement.style.setProperty('--ad-banner-h', h + 'px');
      log.info('banner size:', size);
    }
  });

  // Interstitial.
  AdMob.addListener?.('interstitialAdLoaded', () => {
    interstitial.state = 'ready';
    interstitial.retryAttempts = 0;
    log.info('interstitial ready');
  });
  AdMob.addListener?.('interstitialAdFailedToLoad', (/** @type {any} */ e) => {
    log.warn('interstitial load failed:', e?.message ?? e);
    handleLoadFailure(interstitial, preloadInterstitial);
  });
  AdMob.addListener?.('interstitialAdDismissed', () => {
    log.info('interstitial dismissed → preload next');
    interstitial.state = 'idle';
    void preloadInterstitial();
  });
  AdMob.addListener?.('interstitialAdShowed', () => {
    interstitial.state = 'showing';
  });

  // Rewarded. v7 event names use the `on` prefix (RewardAdPluginEvents enum).
  AdMob.addListener?.('onRewardedVideoAdLoaded', () => {
    rewarded.state = 'ready';
    rewarded.retryAttempts = 0;
    log.info('rewarded ready');
  });
  AdMob.addListener?.('onRewardedVideoAdFailedToLoad', (/** @type {any} */ e) => {
    log.warn('rewarded load failed:', e?.message ?? e);
    handleLoadFailure(rewarded, preloadRewarded);
  });
  AdMob.addListener?.('onRewardedVideoAdReward', (/** @type {any} */ data) => {
    log.info('rewarded earned:', data);
    if (rewarded.pendingResolve) {
      rewarded.pendingResolve({ rewarded: true, skipped: false });
      rewarded.pendingResolve = null;
    }
  });
  AdMob.addListener?.('onRewardedVideoAdDismissed', () => {
    log.info('rewarded dismissed → preload next');
    if (rewarded.pendingResolve) {
      // User closed without earning reward.
      rewarded.pendingResolve({ rewarded: false, skipped: false });
      rewarded.pendingResolve = null;
    }
    rewarded.state = 'idle';
    void preloadRewarded();
  });
}

/**
 * Common path when a `prepare*` call or `*FailedToLoad` event fires. Retries
 * with exponential backoff up to `maxAttempts`, then gives up silently.
 *
 * @param {AdRecord} record
 * @param {() => Promise<void>} reloader
 */
function handleLoadFailure(record, reloader) {
  record.state = 'failed';
  record.retryAttempts += 1;
  if (record.retryAttempts > Cfg.retry.maxAttempts) {
    log.warn('giving up after', Cfg.retry.maxAttempts, 'attempts');
    return;
  }
  const delay = Math.min(
    Cfg.retry.maxDelayMs,
    Cfg.retry.baseDelayMs * 2 ** (record.retryAttempts - 1),
  );
  log.info(`retrying in ${delay}ms (attempt ${record.retryAttempts}/${Cfg.retry.maxAttempts})`);
  void sleep(delay).then(() => { void reloader(); });
}

/**
 * @param {AdRecord} record
 * @param {AdState} target
 * @param {number} timeoutMs
 * @returns {Promise<boolean>}
 */
async function waitForState(record, target, timeoutMs) {
  const start = Date.now();
  while (record.state !== target && Date.now() - start < timeoutMs) {
    await sleep(100);
  }
  return record.state === target;
}

/**
 * Inspect the current state — useful for QA and "show ad when ready" UI logic.
 */
export function status() {
  return {
    available: isAvailable(),
    initialised,
    banner: { visible: bannerVisible },
    interstitial: { state: interstitial.state, retryAttempts: interstitial.retryAttempts },
    rewarded:     { state: rewarded.state,     retryAttempts: rewarded.retryAttempts },
  };
}
